import img from "../assets/images/shutterstock_699442570-min.jpg";
import { useState } from "react";
import constants from "../constants/constants";
import SEO, { pageSEO } from "../components/seo.component";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../functions/animations";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartLine, faFileLines, faHouse } from "@fortawesome/free-solid-svg-icons";

export default function Reports() {
  const [popup, setPopup] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    clientType: "",
    interests: [],
  });

  const seoConfig = pageSEO.reports();

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckboxChange = (e) => {
    const { value, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      interests: checked
        ? [...prev.interests, value]
        : prev.interests.filter((item) => item !== value),
    }));
  };

  async function handleSubmit(event) {
    event.preventDefault();

    const submissionData = {
      access_key: constants.EMAIL_ACCESS_KEY,
      subject: "New Lead Interested in Real Estate Reports",
      Name: formData.name,
      "Email Address": formData.email,
      "Client Type": formData.clientType,
      "Interested in": formData.interests,
    };

    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(submissionData),
      });

      const data = await res.json();

      setPopup(
        data?.success
          ? {
            subject: "Thanks for your message!",
            message: "We will get back to you as soon as possible.",
          }
          : {
            subject: "Something went wrong",
            message: "Please try again later.",
          }
      );
    } catch {
      setPopup({
        subject: "Something went wrong",
        message: "Please try again later.",
      });
    }
  }

  return (
    <>
      <SEO {...seoConfig} />

      {/* HERO */}
      <section className="relative overflow-hidden bg-[#fffdfa] pt-40 pb-12 lg:pb-28">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mx-auto max-w-[1200px] px-6 lg:px-16"
        >
          <motion.div
            variants={fadeUp}
            className="flex items-center justify-center gap-5 mb-8"
          >
            <div className="h-px w-16 bg-[#8b8172]" />

            <span className="text-xs text-center uppercase tracking-[0.45em] text-[#6d655b]">
              Exclusive Resources
            </span>

            <div className="h-px w-16 bg-[#8b8172]" />
          </motion.div>

          <div className="text-center">
            <motion.h1
              variants={fadeUp}
              className="font-serif text-5xl leading-[1.05] text-[#2b2925] lg:text-7xl"
            >
              Real Estate
              <br />
              Reports
            </motion.h1>

            <motion.p
              variants={fadeUp}
              className="mx-auto mt-10 max-w-3xl text-lg leading-9 text-[#5f594f]"
            >
              Receive valuable market updates, buying insights, and selling
              strategies designed to help you make confident real estate
              decisions.
            </motion.p>
          </div>
        </motion.div>
      </section>

      {/* CONTENT */}
      <section className="bg-[#fffdfa] pb-32">
        <div className="mx-auto max-w-[1200px] px-6 lg:px-16">

          <div className="grid gap-8 lg:grid-cols-[450px_1fr]">
            {/* IMAGE */}
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className=""
            >
              <div className="sticky top-32">
                <div className="relative">
                  <div className="absolute -left-9 -top-9 h-full w-full border-[15px] border-[#8b8172]/40 hidden lg:block" />

                  <img
                    src={img}
                    alt="Real Estate Reports"
                    className="relative h-[450px] lg:h-[650px] w-full object-cover"
                  />

                  <div className="absolute bottom-0 left-0 bg-[#d3d2d0] px-8 py-6">
                    <p className="font-serif text-3xl text-[#2b2925]">
                      Market
                    </p>

                    <p className="mt-2 text-xs uppercase tracking-[0.35em] text-[#6d655b]">
                      Insights & Trends
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* FORM */}
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <div className="border border-[#d8d0c6] bg-white p-8 md:p-12">
                <div className="mb-10">
                  <p className="text-xs uppercase tracking-[0.4em] text-[#8b8172]">
                    Personalized Resources
                  </p>

                  <h2 className="mt-5 font-serif text-4xl text-[#2b2925]">
                    Request Your Reports
                  </h2>

                  <p className="mt-4 leading-8 text-[#5f594f]">
                    Tell us what information you are looking for and we will send
                    personalized resources directly to your inbox.
                  </p>
                </div>

                <form
                  onSubmit={handleSubmit}
                  className="space-y-6"
                >
                  <div className="grid gap-6 md:grid-cols-2">
                    <input
                      required
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Full Name"
                      className="w-full border border-[#d8d0c6] bg-[#fffdfa] px-5 py-4 text-[#2b2925] outline-none transition focus:border-[#8b8172]"
                    />

                    <input
                      required
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Email Address"
                      className="w-full border border-[#d8d0c6] bg-[#fffdfa] px-5 py-4 text-[#2b2925] outline-none transition focus:border-[#8b8172]"
                    />
                  </div>

                  <select
                    required
                    name="clientType"
                    value={formData.clientType}
                    onChange={handleInputChange}
                    className="w-full border border-[#d8d0c6] bg-[#fffdfa] px-5 py-4 text-[#2b2925] outline-none transition focus:border-[#8b8172]"
                  >
                    <option value="">
                      I am a
                    </option>

                    <option>
                      Buyer
                    </option>

                    <option>
                      Seller
                    </option>

                    <option>
                      Renter
                    </option>

                    <option>
                      Other
                    </option>
                  </select>
                  <div>

                    <p className="mb-5 text-xs uppercase tracking-[0.35em] text-[#6d655b]">
                      Interested In
                    </p>

                    <div className="grid gap-4 md:grid-cols-2">
                      {
                        [
                          "Buying guides",
                          "Selling guides",
                          "Choosing realtor",
                          "Renovations",
                        ].map((item) => (

                          <label
                            key={item}
                            className={`
                          cursor-pointer
                          border
                          p-5
                          transition-all
                          duration-300
                          ${formData.interests.includes(item)
                                ? "border-[#8b8172] bg-[#eee9df]"
                                : "border-[#d8d0c6] hover:border-[#8b8172]"
                              }
                        `}
                          >
                            <div className="flex items-center gap-3">
                              <input
                                type="checkbox"
                                value={item}
                                checked={formData.interests.includes(item)}
                                onChange={handleCheckboxChange}
                                className="h-4 w-4 accent-[#2b2925]"
                              />

                              <span className="text-sm text-[#2b2925]">
                                {item}
                              </span>
                            </div>
                          </label>
                        ))
                      }
                    </div>
                  </div>

                  <button
                    className="border border-[#2b2925] px-10 py-4 text-xs  uppercase tracking-[0.35em] text-[#2b2925] transition hover:bg-[#2b2925] hover:text-[#fffdfa]"
                  >
                    Request Reports
                  </button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* POPUP */}
      {popup && (
        <>
          <div className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm" />

          <div className="fixed left-1/2 top-1/2 z-50 w-[90%] max-w-md -translate-x-1/2 -translate-y-1/2 bg-white p-10 text-center shadow-2xl">
            <div className="mx-auto flex h-16 w-16 items-center justify-center border border-[#8b8172] text-2xl text-[#8b8172]">
              ✓
            </div>

            <h3 className="mt-6 font-serif text-3xl text-[#2b2925]">
              {popup.subject}
            </h3>

            <p className="mt-4 leading-8 text-[#5f594f]">
              {popup.message}
            </p>

            <button
              onClick={() => setPopup(false)}
              className="mt-8 border border-[#2b2925] px-8 py-3 text-xs uppercase tracking-[0.3em] text-[#2b2925] transition hover:bg-[#2b2925] hover:text-white"
            >
              Close
            </button>
          </div>
        </>
      )}
    </>
  );
}